import type {ReactNode} from 'react'
import {cn} from '@/lib/utils'

type StatBoxVariant = 'success' | 'danger' | 'neutral' | 'dangerSoft' | 'plain'
type StatBoxLayout = 'stack' | 'split'

interface StatBoxProps {
    variant?: StatBoxVariant
    /**
     * `stack` (default) centers the content — used for summary stat tiles.
     * `split` lays children out in a row with space-between — used for bordered list rows
     * where a label is on the left and a badge / action on the right.
     */
    layout?: StatBoxLayout
    /** When true, adds a hover background — used for list-row variants of `plain`. */
    interactive?: boolean
    children: ReactNode
    className?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const variantClasses: Record<StatBoxVariant, string> = {
    success: 'bg-status-success-subtle border-green-200 dark:border-green-800',
    danger: 'bg-status-danger-subtle border-red-200 dark:border-red-800',
    neutral: 'bg-gray-50 border-gray-200 dark:bg-muted dark:border-border',
    dangerSoft: 'bg-destructive/5 border-destructive/20',
    plain: '',
}

const layoutClasses: Record<StatBoxLayout, string> = {
    stack: 'text-center',
    split: 'flex items-center justify-between gap-2',
}
/* eslint-enable i18next/no-literal-string */

export function StatBox({variant = 'neutral', layout = 'stack', interactive, children, className}: StatBoxProps) {
    return (
        <div
            className={cn(
                'rounded-lg border p-3',
                layoutClasses[layout],
                variantClasses[variant],
                interactive && 'hover:bg-muted/50',
                className,
            )}
        >
            {children}
        </div>
    )
}
